import { lazy } from 'react';
import { createRoute, type AnyRootRoute } from '@tanstack/react-router';

import { issueSearchSchema } from '@pages/issue/Issue.hooks';
import { useAuth } from '@shared/auth';

/**
 * Canonical, reusable routes shared across templates -- the "route catalog"
 * named in improvements/07-template-architecture.md's actionable plan step 3.
 * Each entry here points at a component genuinely shared by two or more
 * templates today (confirmed by reading every `App.router.tsx`, not
 * assumed) -- everything template-exclusive stays out of this file and is
 * passed to `createTenantApp`'s `custom` escape hatch instead.
 *
 * `home` is deliberately NOT here: every template's home page is bespoke UI
 * (each has its own home/Home.tsx), so it stays a manifest-supplied slot in
 * createTenantApp instead. `issueGuarded`/`issueArticleGuarded` (the
 * Cognito-`beforeLoad`-gated variant used by basic/magazine/magshop) live in
 * `buildGuardedIssueRoutes` below, not here, since they need a per-template
 * auth adapter injected -- they can't be a fixed catalog entry.
 */

const LazyIssue = lazy(() => import('@pages/issue/Issue'));
const LazyDrafts = lazy(() => import('@pages/drafts/Drafts'));
const LazyFavorite = lazy(() => import('@pages/favorite/Favorite'));
const LazyCallback = lazy(() => import('@features/oauth-callback/Callback'));
const LazyLogout = lazy(() => import('@features/login/Logout'));
const LazySubscriptions = lazy(
	() => import('@features/manage-subscription/Subscriptions'),
);
const LazySubscriptionSuccess = lazy(
	() => import('@features/manage-subscription/success/Success'),
);
const LazyPreview = lazy(() => import('@pages/preview-basic/Preview'));
const LazyPreviewView = lazy(() => import('@pages/preview/Preview'));

// `useAuth` no-ops unless config.app.auth.type === 'token' (see
// shared/auth/Authentication.hooks.tsx), so wrapping the issue routes
// with it here is safe for every auth type, not just the templates (poets,
// morningstar, sections) that currently repeat this exact wrapper by hand.
function AuthenticatedIssue() {
	useAuth({ redirect: true });
	return <LazyIssue />;
}

export function buildRouteCatalog(rootRoute: AnyRootRoute) {
	const issueRoute = createRoute({
		getParentRoute: () => rootRoute,
		component: AuthenticatedIssue,
		validateSearch: issueSearchSchema,
		path: 'issues/$id',
	});

	const issueArticleRoute = createRoute({
		getParentRoute: () => issueRoute,
		component: AuthenticatedIssue,
		validateSearch: issueSearchSchema,
		path: '$articleId',
	});

	// Admin-gated in every template that has it (basic, daily, magazine,
	// magshop, morningstar, padi, poets, sections, starmagazine, toyota --
	// all ten) via `if (config?.app?.admin) routes.push(draftsRoute)`.
	// createTenantApp applies that same filter, not this file.
	const draftsRoute = createRoute({
		getParentRoute: () => rootRoute,
		component: LazyDrafts,
		path: 'drafts',
	});

	// Shared by morningstar + poets, both gated by config.app.favorite.
	const favoriteRoute = createRoute({
		getParentRoute: () => rootRoute,
		component: LazyFavorite,
		path: 'favorite/$id',
	});

	// Shared by basic, magazine, daily, starmagazine -- confirmed identical
	// shape (no validateSearch) across all four. magshop has its own,
	// differently-shaped callback (validateSearch: callbackSearchSchema) and
	// padi has its own separate fork; neither opts into this entry.
	const callbackRoute = createRoute({
		getParentRoute: () => rootRoute,
		component: LazyCallback,
		path: 'callback',
	});

	// Shared by basic, magazine, daily, starmagazine. magshop has its own
	// Logout backed by its own useLogout/Auth.hooks -- not this entry.
	const logoutRoute = createRoute({
		getParentRoute: () => rootRoute,
		component: LazyLogout,
		path: 'logout',
	});

	// Shared by basic, magazine, daily, starmagazine.
	const subscriptionsRoute = createRoute({
		getParentRoute: () => rootRoute,
		component: LazySubscriptions,
		path: 'subscriptions',
	});

	// Deliberate, reviewed drift fix (same shape as sections' `drafts` fix in
	// the previous migration pass): basic/magazine already nested `success`
	// under `subscriptionsRoute`; daily/starmagazine instead declared it as
	// a root-level sibling with the literal path `subscriptions/success`.
	// Both resolve to the same fullPath ('/subscriptions/success'), but the
	// catalog can only give a route one parent -- nesting under
	// `subscriptionsRoute` is the shape two of the four already used, so
	// daily/starmagazine move to match rather than the other way around.
	const subscriptionsSuccessRoute = createRoute({
		getParentRoute: () => subscriptionsRoute,
		component: LazySubscriptionSuccess,
		path: 'success',
	});

	// `@pages/preview-basic/Preview` -- shared by basic + magazine.
	const previewRoute = createRoute({
		getParentRoute: () => rootRoute,
		component: LazyPreview,
		path: 'preview/$id',
	});

	// `@pages/preview/Preview` -- a *different* component, shared by
	// morningstar + poets. Not the same implementation as `preview` above;
	// kept as a separate entry rather than forced together.
	const previewViewRoute = createRoute({
		getParentRoute: () => rootRoute,
		component: LazyPreviewView,
		path: 'preview/$id',
	});

	return {
		issue: issueRoute,
		issueArticle: issueArticleRoute,
		drafts: draftsRoute,
		favorite: favoriteRoute,
		callback: callbackRoute,
		logout: logoutRoute,
		subscriptions: subscriptionsRoute,
		subscriptionsSuccess: subscriptionsSuccessRoute,
		preview: previewRoute,
		previewView: previewViewRoute,
	};
}

export type RouteCatalog = ReturnType<typeof buildRouteCatalog>;
export type RouteCatalogKey = keyof RouteCatalog;

/**
 * The Cognito-`beforeLoad`-gated issue/issueArticle pair used identically by
 * basic, magazine, and magshop: same shared `@pages/issue/Issue` component,
 * same `beforeLoad: () => ensureAuthenticated(callbackBasename)` guard, same
 * `onClickLogOut` prop wiring -- but each template's `ensureAuthenticated`/
 * `logOut` come from a different Cognito instance (basic and magazine share
 * one via `@features/login`; magshop deliberately keeps its own, per
 * services/auth/cognito.ts's doc comment). Parameterized by an adapter
 * instead of being a fixed catalog entry, since the *wiring pattern* is
 * shared but the underlying auth implementation isn't.
 */
export interface GuardedIssueAdapter {
	ensureAuthenticated: (basename: string) => Promise<unknown>;
	useOnClickLogOut: () => ((url?: string) => void) | undefined;
	callbackBasename: string;
}

export function buildGuardedIssueRoutes(
	rootRoute: AnyRootRoute,
	adapter: GuardedIssueAdapter,
) {
	function GuardedIssue() {
		return <LazyIssue onClickLogOut={adapter.useOnClickLogOut()} />;
	}

	const issueGuardedRoute = createRoute({
		getParentRoute: () => rootRoute,
		beforeLoad: () => adapter.ensureAuthenticated(adapter.callbackBasename),
		component: GuardedIssue,
		validateSearch: issueSearchSchema,
		path: 'issues/$id',
	});

	const issueArticleGuardedRoute = createRoute({
		getParentRoute: () => issueGuardedRoute,
		beforeLoad: () => adapter.ensureAuthenticated(adapter.callbackBasename),
		component: GuardedIssue,
		validateSearch: issueSearchSchema,
		path: '$articleId',
	});

	return {
		issueGuarded: issueGuardedRoute,
		issueArticleGuarded: issueArticleGuardedRoute,
	};
}
